<template>
  <div class="px-2.5 py-3 border-2 border-secondary-600  rounded-md w-full">
    <div class="flex items-center">
      <img
        :alt="name"
        class="w-14 h-14 rounded-full object-center"
        :src="imageUrl"
      />
      <div class="w-full ml-2">
        <div class="flex justify-between w-full items-center mb-4">
          <p class="text-gray-700 font-small font-semibold">{{ name }}</p>
          <button type="button" @click="$emit('remove', id)" class="px-2 py-2 text-xs font-bold text-center inline-flex items-center text-red-600 bg-white rounded-lg bg-gray-50 hover:bg-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-300">
            <svg v-if="false" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
        <div class="flex justify-between w-full">
          <p class="text-gray-900 font-bold">{{ currency }}{{ price }}</p>
          <div class="flex space-x-4 items-center">
            <button
              @click="modifyQuantity(-1)"
              :disabled="localQuantity === 1"
              :class="decreamentQuantityButtonClasses"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 12h-15" />
              </svg>
            </button>
            <div>{{ localQuantity }}</div>
            <button
              @click="modifyQuantity(1)"
              class="border border-secondary-600 hover:border-primary-600 inline-flex items-center justify-center font-medium text-base focus-visible:outline focus-visible:outline-offset rounded-md disabled:text-disabled-500 disabled:bg-disabled-300 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed disabled:text-gray-400 p-1 gap-2 text-secondary-600 hover:bg-primary-600 hover:text-secondary-600 active:bg-primary-600 active:text-secondary-600 disabled:bg-transparent"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  ref, toRefs, watch,
} from 'vue';

const emit = defineEmits([
  'update-quantity',
  'remove',
]);
const props = defineProps({
  currency: {
    required: true,
    type: String,
  },
  id: {
    required: true,
    type: String,
  },
  name: {
    required: true,
    type: String,
  },
  imageUrl: {
    required: true,
    type: String,
  },
  price: {
    type: Number,
    required: true,
  },
  productId: {
    type: String,
    required: true,
  },
  quantity: {
    type: Number,
    required: true,
  },
});
const {
  quantity,
} = toRefs(props);
const localQuantityDebounceTimer = ref(null);
const localQuantity = ref(quantity.value);

const decreamentQuantityButtonClasses = computed(() => {
  const classes = [
    'border',
    'inline-flex',
    'items-center',
    'justify-center',
    'font-medium',
    'text-base',
    'focus-visible:outline',
    'focus-visible:outline-offset',
    'rounded-md',
    'disabled:text-disabled-500',
    'disabled:bg-disabled-300',
    'disabled:shadow-none',
    'disabled:ring-0',
    'disabled:cursor-not-allowed',
    'disabled:text-gray-400',
    'p-1',
    'gap-2',
    'text-secondary-600',
    'hover:bg-primary-600',
    'hover:text-secondary-600',
    'active:bg-primary-600',
    'active:text-secondary-600',
    'disabled:bg-transparent',
  ];
  if (localQuantity.value === 1) {
    classes.push('border-grey-200');
  } else {
    classes.push('border-secondary-600');
  }
  return classes;
});
const modifyQuantity = (value) => {
  if ((localQuantity.value + value) === 0) {
    return;
  }
  localQuantity.value = parseInt(localQuantity.value + value, 10);
};

watch(localQuantity, (newV) => {
  if (localQuantityDebounceTimer.value) {
    clearTimeout(localQuantityDebounceTimer.value);
  }
  localQuantityDebounceTimer.value = setTimeout(() => {
    emit('update-quantity', {
      id: props.id,
      productId: props.productId,
      quantity: newV,
    });
  }, 300);
});
</script>

<style>
input#quantity {
  -moz-appearance: textfield;
}
</style>
